import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ChevronDown, Globe, Cpu, Zap, Archive } from 'lucide-react';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const navGroups = {
    product: [
      { label: "Technology", href: "/#technology", icon: Cpu },
      { label: "Pricing", href: "/#pricing", icon: Zap },
      { label: "ROI Calculator", href: "/#calculator", icon: Zap },
    ],
    archives: [
      { label: "Blog", to: "/blog", icon: Archive },
      { label: "Research", to: "/research", icon: Globe },
    ],
  };

  return (
    <>
      <nav className={`fixed w-full z-50 top-0 transition-all duration-500 ${
        scrolled 
          ? 'py-3 backdrop-blur-xl border-b border-teal-500/20 bg-slate-950/80 shadow-2xl shadow-teal-500/5' 
          : 'py-6 bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          {/* Logo Section */}
          <div className="flex items-center gap-8">
            <Link to="/" className="flex items-center cursor-pointer group" aria-label="GreenTick Home">
              <picture>
                <source srcSet="/greenwithtick_whitetext.webp 1x, /greenwithtick_whitetext.webp 2x" type="image/webp" />
                <img 
                  src="/greenwithtick_whitetext.png" 
                  alt="GreenTick" 
                  width="91"
                  height="40"
                  className="h-10 w-auto group-hover:scale-105 transition-transform drop-shadow-[0_0_12px_rgba(20,184,166,0.3)]" 
                />
              </picture>
            </Link>
          </div>

          {/* Global Navigation (Desktop) */}
          <div className="hidden md:flex items-center gap-10">
            <div className="flex items-center gap-8 text-[11px] font-black uppercase tracking-[0.15em]">
              {/* Product Section */}
              <div className="relative group/nav flex items-center gap-1 text-slate-400 hover:text-teal-400 cursor-pointer transition-colors py-2">
                Product <ChevronDown className="w-3 h-3 group-hover/nav:rotate-180 transition-transform" />
                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-4 opacity-0 translate-y-2 pointer-events-none group-hover/nav:opacity-100 group-hover/nav:translate-y-0 group-hover/nav:pointer-events-auto transition-all duration-300">
                  <div className="bg-slate-900/95 backdrop-blur-2xl border border-white/10 rounded-2xl p-4 w-48 shadow-2xl">
                    {navGroups.product.map((item, i) => (
                      <a key={i} href={item.href} className="flex items-center gap-3 p-3 rounded-xl hover:bg-teal-500/10 text-slate-400 hover:text-teal-400 transition-all border border-transparent hover:border-teal-500/20 mb-1 last:mb-0">
                        <item.icon className="w-4 h-4" />
                        {item.label}
                      </a>
                    ))}
                  </div>
                </div>
              </div>

              {/* Archive Section */}
              <div className="relative group/archive flex items-center gap-1 text-slate-400 hover:text-teal-400 cursor-pointer transition-colors py-2">
                Resources <ChevronDown className="w-3 h-3 group-hover/archive:rotate-180 transition-transform" />
                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-4 opacity-0 translate-y-2 pointer-events-none group-hover/archive:opacity-100 group-hover/archive:translate-y-0 group-hover/archive:pointer-events-auto transition-all duration-300">
                  <div className="bg-slate-900/95 backdrop-blur-2xl border border-white/10 rounded-2xl p-4 w-48 shadow-2xl">
                    {navGroups.archives.map((item, i) => (
                      <Link key={i} to={item.to!} className="flex items-center gap-3 p-3 rounded-xl hover:bg-teal-500/10 text-slate-400 hover:text-teal-400 transition-all border border-transparent hover:border-teal-500/20 mb-1 last:mb-0">
                        <item.icon className="w-4 h-4" />
                        {item.label}
                      </Link>
                    ))}
                    <a href="/integrations" className="flex items-center gap-3 p-3 rounded-xl hover:bg-teal-500/10 text-slate-400 hover:text-teal-400 transition-all border border-transparent hover:border-teal-500/20 mt-1">
                      <Globe className="w-4 h-4" />
                      Integrations
                    </a>
                  </div>
                </div>
              </div>

              <Link to="/about" className="text-slate-400 hover:text-teal-400 transition-colors">About</Link>
              
              <a href="/#calculator" className="text-slate-400 hover:text-teal-400 transition-colors">ROI Calculator</a>
              
              <a 
                href="https://sandeshlekhwani.github.io/GreentickCandidateReports/sandeshgtreport.html" 
                target="_blank" 
                rel="noopener noreferrer"
                className="px-4 py-1.5 rounded-full bg-teal-500/10 border border-teal-500/20 text-teal-400 hover:bg-teal-500/20 transition-all font-black"
              >
                See Sample Report
              </a>
            </div>

            <div className="h-6 w-px bg-white/10 mx-2"></div>

            {/* CTA Group */}
            <div className="flex items-center">
              <a 
                href="https://dashboard.greentick.org.in/auth" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="relative group px-8 py-3 rounded-xl bg-gradient-to-r from-teal-500 to-emerald-500 text-white font-black uppercase tracking-[0.2em] text-[10px] overflow-hidden hover:shadow-2xl hover:shadow-teal-500/50 transition-all hover:scale-105"
              >
                <span className="relative z-10">Register / Login</span>
                <div className="absolute top-0 -left-[100%] w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent group-hover:left-[100%] transition-all duration-1000"></div>
              </a>
            </div>
          </div>

          {/* Mobile Toggle */}
          <button
            className="md:hidden text-white p-2.5 rounded-xl bg-teal-500/5 border border-teal-500/20 hover:bg-teal-500/10 transition-all"
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? "Close main menu" : "Open main menu"}
          >
            {isOpen ? <X className="w-6 h-6 text-teal-400" /> : <Menu className="w-6 h-6 text-slate-400" />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu — top-level overlay, z-[100] to beat all other elements */}
      {isOpen && (
        <div
          className="md:hidden fixed inset-0 z-[100] flex flex-col backdrop-blur-3xl"
          style={{ backgroundColor: 'rgba(2, 8, 28, 0.88)' }}
        >
          {/* Menu Header */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-white/10 flex-shrink-0">
            <Link to="/" onClick={() => setIsOpen(false)} aria-label="GreenTick Home">
              <picture>
                <source srcSet="/greenwithtick_whitetext.webp 1x, /greenwithtick_whitetext.webp 2x" type="image/webp" />
                <img
                  src="/greenwithtick_whitetext.png"
                  alt="GreenTick"
                  width="91"
                  height="32"
                  className="h-8 w-auto"
                />
              </picture>
            </Link>
            <button
              onClick={() => setIsOpen(false)}
              className="p-2.5 rounded-xl bg-teal-500/10 border border-teal-500/20 text-teal-400"
              aria-label="Close mobile menu"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Menu Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="flex flex-col gap-8">

              {/* Product */}
              <div>
                <p className="text-[10px] font-black text-teal-500 uppercase tracking-[0.2em] mb-3">Product</p>
                <div className="flex flex-col gap-2">
                  {navGroups.product.map((item, i) => (
                    <a
                      key={i}
                      href={item.href}
                      onClick={() => setIsOpen(false)}
                      className="flex items-center gap-4 p-4 rounded-2xl border border-white/8 text-slate-300 hover:text-white hover:border-teal-500/30 hover:bg-teal-500/5 transition-all"
                      style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}
                    >
                      <item.icon className="w-5 h-5 text-teal-500 flex-shrink-0" />
                      <span className="text-sm font-bold uppercase tracking-wider">{item.label}</span>
                    </a>
                  ))}
                </div>
              </div>

              {/* Resources */}
              <div>
                <p className="text-[10px] font-black text-teal-500 uppercase tracking-[0.2em] mb-3">Resources</p>
                <div className="flex flex-col gap-2">
                  {navGroups.archives.map((item, i) => (
                    <Link
                      key={i}
                      to={item.to!}
                      onClick={() => setIsOpen(false)}
                      className="flex items-center gap-4 p-4 rounded-2xl border border-white/8 text-slate-300 hover:text-white hover:border-emerald-500/30 hover:bg-emerald-500/5 transition-all"
                      style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}
                    >
                      <item.icon className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                      <span className="text-sm font-bold uppercase tracking-wider">{item.label}</span>
                    </Link>
                  ))}
                </div>
              </div>

              {/* CTAs */}
              <div className="pt-4 border-t border-white/10 flex flex-col gap-3">
                <a
                  href="https://dashboard.greentick.org.in/auth"
                  onClick={() => setIsOpen(false)}
                  className="w-full py-4 rounded-2xl bg-gradient-to-r from-teal-500 to-emerald-500 text-white font-black uppercase tracking-[0.2em] text-center text-xs shadow-2xl shadow-teal-500/20"
                >
                  Register / Login
                </a>
                <a
                  href="https://sandeshlekhwani.github.io/GreentickCandidateReports/sandeshgtreport.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsOpen(false)}
                  className="w-full py-4 rounded-2xl border border-teal-500/30 text-teal-400 font-black uppercase tracking-widest text-center text-[10px]"
                >
                  See Sample Report
                </a>
              </div>

            </div>
          </div>
        </div>
      )}
    </>
  );
}
